import React, { useCallback, useState } from "react";
import client from "@culturehq/client";
import { ActionButton, Button, Loader, Pagination, Success, Table, Tooltip, Warning } from "@culturehq/components";

import { useFetch } from "app/hooks";
import { ProfileLink } from "app/links";
import { errorService, Trans, t } from "app/utils";

import PageHeader from "components/display/PageHeader";
import { Time, TimeDiff } from "components/common/Time";
import ChqModalConfirm from "components/common/ChqModalConfirm";

const FeedbackExport = ({ exported, onExport }) => {
  if (exported) {
    return (
      <Success>
        <Trans>Request received. An email will be sent to you soon.</Trans>
      </Success>
    );
  }

  return (
    <Tooltip tip={t("Export all of the feedback from the platform. Once the list has been processed, an email will be sent to you with your data in a .csv file.")}>
      <Button primary onClick={onExport} icon="ios-download-outline">
        <Trans>Export Feedback</Trans>
      </Button>
    </Tooltip>
  );
};

const FeedbackReview = ({ feedback, onUpdate }) => {
  const onReview = useCallback(
    async () => {
      try {
        const response = await client.reviewFeedback({ feedbackId: feedback.id });

        onUpdate(({ feedbacks }) => ({
          feedbacks: feedbacks.map(current => (
            current.id === feedback.id ? response.feedback : current
          ))
        }));
      } catch (error) {
        errorService.error(error);
      }
    },
    [feedback, onUpdate]
  );

  if (feedback.reviewedAt) {
    return (
      <i className="pull-right text-right">
        <Trans>Reviewed <Time time={feedback.reviewedAt} format="%Y/%m/%d" /></Trans>
      </i>
    );
  }

  return (
    <ChqModalConfirm
      onAccept={onReview}
      trigger={onTrigger => (
        <ActionButton className="pull-right" onClick={onTrigger}>
          <Trans>Mark as Reviewed</Trans>
        </ActionButton>
      )}
    >
      <Trans>Mark this feedback as reviewed?</Trans>
    </ChqModalConfirm>
  );
};

const FeedbackRow = ({ feedback, onUpdate }) => (
  <tr>
    <td>
      {feedback.body}
    </td>
    <td>
      {
        feedback.anonymous
          ? <i>anonymous</i>
          : <ProfileLink user={feedback.user} />
      }
    </td>
    <td>
      <TimeDiff time={feedback.createdAt} />
    </td>
    <td>
      <FeedbackReview feedback={feedback} onUpdate={onUpdate} />
    </td>
  </tr>
);

const FeedbackTable = ({ feedbacks, onUpdate }) => (
  <Table>
    <thead>
      <tr>
        <th>
          <Trans>Message</Trans>
        </th>
        <th>
          <Trans>User</Trans>
        </th>
        <th>
          <Trans>Submitted</Trans>
        </th>
        <th className="pull-right">
          <Trans>Actions</Trans>
        </th>
      </tr>
    </thead>
    <tbody>
      {feedbacks.map(feedback => (
        <FeedbackRow key={feedback.id} feedback={feedback} onUpdate={onUpdate} />
      ))}
    </tbody>
  </Table>
);

const Feedback = () => {
  const [page, setPage] = useState(1);
  const { fetching, json, onUpdate } = useFetch(() => client.listFeedbacks({ page }), [page]);
  const { feedbacks = [], pagination } = json;

  const [exported, setExported] = useState(false);
  const onExport = useCallback(
    () => {
      setExported(true);
      return client.exportFeedback();
    },
    [setExported]
  );

  return (
    <div className="padding-left-15px padding-right-15px">
      <PageHeader title={t("Review Feedback")}>
        <Trans>
          Read and review feedback that your users have submitted. Mark
          addressed feedback as “Reviewed” to inform other administrators that
          the feedback has been considered.
        </Trans>
      </PageHeader>
      <p>
        <FeedbackExport exported={exported} onExport={onExport} />
      </p>
      <Loader loading={fetching}>
        <FeedbackTable feedbacks={feedbacks} onUpdate={onUpdate} />

        {feedbacks.length < 1 && (
          <Warning>
            <Trans><strong>Uh oh!</strong> No results to show.</Trans>
          </Warning>
        )}

        <Pagination {...pagination} onClick={setPage} />
      </Loader>
    </div>
  );
};

export default Feedback;
